import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Download, Assessment, TrendingUp, People, AttachMoney } from '@mui/icons-material';
import { useQuery } from 'react-query';
import { reportsAPI } from '../../services/api';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import numeral from 'numeral';

const ReportsContent = () => {
  const [reportType, setReportType] = useState('comprehensive');
  const [timeRange, setTimeRange] = useState('30');
  const [format, setFormat] = useState('json');

  const { data: comprehensiveData, isLoading: comprehensiveLoading } = useQuery(
    ['reports-comprehensive', timeRange, format],
    () => reportsAPI.getComprehensive({
      startDate: new Date(Date.now() - parseInt(timeRange) * 24 * 60 * 60 * 1000).toISOString(),
      format
    }),
    { enabled: reportType === 'comprehensive' }
  );

  const { data: revenueData, isLoading: revenueLoading } = useQuery(
    ['reports-revenue', timeRange],
    () => reportsAPI.getRevenue({ months: Math.ceil(parseInt(timeRange) / 30) }),
    { enabled: reportType === 'revenue' }
  );

  const { isLoading: userBehaviorLoading } = useQuery(
    ['reports-user-behavior', timeRange],
    () => reportsAPI.getUserBehavior({ days: parseInt(timeRange) }),
    { enabled: reportType === 'user-behavior' }
  );

  const { isLoading: performanceLoading } = useQuery(
    ['reports-performance', timeRange],
    () => reportsAPI.getPerformance({ days: parseInt(timeRange) }),
    { enabled: reportType === 'performance' }
  );

  const handleDownloadReport = () => {
    console.log('Downloading report:', reportType, timeRange, format);
  };

  const isLoading = comprehensiveLoading || revenueLoading || userBehaviorLoading || performanceLoading;

  const reportTypes = [
    { value: 'comprehensive', label: 'Comprehensive Report', icon: <Assessment /> },
    { value: 'revenue', label: 'Revenue Report', icon: <AttachMoney /> },
    { value: 'user-behavior', label: 'User Behavior Report', icon: <People /> },
    { value: 'performance', label: 'Performance Report', icon: <TrendingUp /> },
  ];

  const timeRanges = [
    { value: '7', label: 'Last 7 days' },
    { value: '30', label: 'Last 30 days' },
    { value: '90', label: 'Last 3 months' },
    { value: '365', label: 'Last year' },
  ];

  const formats = [
    { value: 'json', label: 'JSON' },
    { value: 'csv', label: 'CSV' },
  ];

  return (
    <Box>
      {/* Report Configuration */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Report Type</InputLabel>
                <Select value={reportType} label="Report Type" onChange={(e) => setReportType(e.target.value)}>
                  {reportTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{type.icon}{type.label}</Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Time Range</InputLabel>
                <Select value={timeRange} label="Time Range" onChange={(e) => setTimeRange(e.target.value)}>
                  {timeRanges.map((range) => (<MenuItem key={range.value} value={range.value}>{range.label}</MenuItem>))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Format</InputLabel>
                <Select value={format} label="Format" onChange={(e) => setFormat(e.target.value)}>
                  {formats.map((fmt) => (<MenuItem key={fmt.value} value={fmt.value}>{fmt.label}</MenuItem>))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button variant="contained" startIcon={<Download />} onClick={handleDownloadReport} fullWidth disabled={isLoading}>
                Download Report
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Report Content */}
      {isLoading ? (
        <LoadingSpinner message="Generating report..." />
      ) : (
        <Grid container spacing={3}>
          {reportType === 'comprehensive' && comprehensiveData && (
            <>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Report Summary</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="h4" color="primary">{numeral(comprehensiveData.data.summary?.installations?.totalInstallations).format('0,0')}</Typography>
                        <Typography variant="body2" color="text.secondary">Total Installations</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="h4" color="secondary">{numeral(comprehensiveData.data.summary?.revenue?.totalRevenue).format('$0,0')}</Typography>
                        <Typography variant="body2" color="text.secondary">Total Revenue</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="h4" color="success.main">{comprehensiveData.data.geographic?.length || 0}</Typography>
                        <Typography variant="body2" color="text.secondary">Countries</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="h4" color="warning.main">{comprehensiveData.data.businessTypes?.length || 0}</Typography>
                        <Typography variant="body2" color="text.secondary">Business Types</Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Top Countries</Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead><TableRow><TableCell>Country</TableCell><TableCell align="right">Users</TableCell></TableRow></TableHead>
                        <TableBody>
                          {comprehensiveData.data.geographic?.slice(0, 5).map((country) => (
                            <TableRow key={country._id}><TableCell>{country._id}</TableCell><TableCell align="right">{country.count}</TableCell></TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            </>
          )}
          {reportType === 'revenue' && revenueData && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Revenue Analysis</Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="h4" color="primary">{numeral(revenueData.data.conversionFunnel?.totalConversions).format('0,0')}</Typography>
                      <Typography variant="body2" color="text.secondary">Total Conversions</Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="h4" color="secondary">{numeral(revenueData.data.churnAnalysis?.avgProfLTV).format('$0,0')}</Typography>
                      <Typography variant="body2" color="text.secondary">Avg Professional LTV</Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="h4" color="success.main">{numeral(revenueData.data.churnAnalysis?.avgEntLTV).format('$0,0')}</Typography>
                      <Typography variant="body2" color="text.secondary">Avg Enterprise LTV</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
};

export default ReportsContent;

